import { useCallback, useEffect, useState } from 'react';
import {
  getStoredThemeMode,
  resolveDark,
  setThemeMode,
  subscribeTheme,
  type ThemeMode,
} from '../utils/theme';

/**
 * React access to the shared theme. Reflects in-app changes (via the module
 * pub-sub) and cross-tab changes (via the storage event), and exposes a simple
 * light/dark `toggle` for the navbar button.
 */
export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(() => getStoredThemeMode());

  useEffect(() => subscribeTheme(setMode), []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'safaqat24.display.theme' || e.key === 'theme') {
        setMode(getStoredThemeMode());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isDark = resolveDark(mode);
  const setTheme = useCallback((next: ThemeMode) => setThemeMode(next), []);
  const toggle = useCallback(() => setThemeMode(isDark ? 'light' : 'dark'), [isDark]);

  return { mode, isDark, setTheme, toggle };
}
